import type { BdfzSession } from '../lib/identity';
import type { GlobalLeaderboardEntry, LeaderboardEntry } from '../lib/leaderboard';

export const PROFILE_EVENT = 'analects-local-profile-changed';
export const PROFILE_KEYS = {
  guest: 'lunyu-battle-guest-v1', alias: 'lunyu-battle-display-alias-v1',
  avatar: 'lunyu-battle-avatar-v1', board: 'lunyu-battle-leaderboard-v1',
} as const;
type Store = Pick<Storage, 'getItem' | 'setItem'>;
export type NativeProfile = { id: string | null; owner: string; kind: 'user' | 'guest'; name: string; alias: string; avatar: string };
function object(value: unknown): value is Record<string, unknown> { return !!value && typeof value === 'object' && !Array.isArray(value); }
function read(store: Store, key: string): unknown {
  const raw = store.getItem(key);
  try { return raw === null ? null : JSON.parse(raw); } catch { throw new Error('profile_storage_invalid'); }
}
function map(store: Store, key: string): Record<string, unknown> {
  const value = read(store, key);
  if (value === null) return {};
  if (!object(value)) throw new Error('profile_storage_invalid');
  return value;
}
export function sessionOwner(session: BdfzSession | null): string {
  if (!session || typeof session.authenticated !== 'boolean') throw new Error('identity_unconfirmed');
  if (!session.authenticated) return 'guest';
  if (!session.user?.slug) throw new Error('identity_unconfirmed');
  return `user:${session.user.slug}`;
}
export async function confirmNativeSession(readSession: () => Promise<BdfzSession>, timeoutMs = 10000): Promise<BdfzSession> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([readSession(), new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('identity_unconfirmed')), timeoutMs);
    })]);
  } finally { if (timer !== undefined) clearTimeout(timer); }
}
export function validAvatar(value: string): string {
  const text = value.trim();
  if (!text) return '';
  // Reject oversize data rather than silently truncating an image or URL.
  if (text.length > 120_000 || !/^(https?:\/\/|data:image\/(?:png|jpe?g|gif|webp|svg\+xml);base64,)/i.test(text)) throw new Error('avatar_invalid');
  return text;
}
export function readNativeProfile(store: Store, session: BdfzSession | null): NativeProfile {
  const owner = sessionOwner(session);
  let id: string | null = owner, name = session?.user?.display_name || session?.user?.displayName || session?.user?.slug || '學子';
  if (owner === 'guest') {
    const guest = read(store, PROFILE_KEYS.guest);
    if (guest === null) { id = null; name = '本機訪客'; }
    else {
      if (!object(guest) || typeof guest.id !== 'string' || !guest.id || typeof guest.name !== 'string' || !guest.name) throw new Error('profile_storage_invalid');
      id = `guest:${guest.id}`; name = guest.name;
    }
  }
  const aliases = map(store, PROFILE_KEYS.alias), avatars = map(store, PROFILE_KEYS.avatar);
  const alias = id && typeof aliases[id] === 'string' ? aliases[id] as string : '';
  const saved = id && typeof avatars[id] === 'string' ? avatars[id] as string : '';
  let avatar = saved || (owner !== 'guest' ? session?.user?.avatar_url || session?.user?.avatarUrl || '' : '');
  try { avatar = validAvatar(avatar); } catch { avatar = ''; } // Preserve unsupported stored bytes, never rewrite them.
  return { id, owner, kind: owner === 'guest' ? 'guest' : 'user', name: alias || name, alias, avatar };
}

/** Only explicit edits write. Identity is rechecked before the synchronous local transaction. */
export async function editNativeProfile(store: Store, expected: NativeProfile, field: 'alias' | 'avatar' | 'create', value: string,
  confirm: () => Promise<BdfzSession>, current: () => boolean): Promise<NativeProfile> {
  const session = await confirmNativeSession(confirm).catch(() => { throw new Error('identity_unconfirmed'); });
  if (!current() || sessionOwner(session) !== expected.owner) throw new Error('account_changed');
  const profile = readNativeProfile(store, session);
  if (profile.id !== expected.id) throw new Error('account_changed');
  if (field === 'create') {
    if (profile.kind !== 'guest' || profile.id) throw new Error('account_changed');
    const id = `guest-${crypto.randomUUID()}`;
    store.setItem(PROFILE_KEYS.guest, JSON.stringify({ id, name: `訪客 ${id.slice(-4).toUpperCase()}` }));
  } else {
    if (!profile.id) throw new Error('guest_profile_required');
    if (field === 'avatar' && profile.kind !== 'guest') throw new Error('central_avatar_owner_contract_required');
    const key = PROFILE_KEYS[field], values = map(store, key);
    const next = field === 'alias' ? value.replace(/\s+/g, ' ').trim().slice(0, 24) : validAvatar(value);
    // Only this exact owner field changes; unknown fields and other accounts survive.
    if (next) Object.defineProperty(values, profile.id, { value: next, enumerable: true, configurable: true, writable: true });
    else delete values[profile.id];
    store.setItem(key, JSON.stringify(values));
  }
  return readNativeProfile(store, session);
}

function row(value: unknown, kind: 'global' | 'guest' | 'user'): boolean {
  return object(value) && value.kind === kind && typeof value.id === 'string' && !!value.id
    && typeof value.name === 'string' && !!value.name && ['mastered', 'gates', 'totalGates', 'percent', 'bestStreak', 'focus'].every(key =>
      typeof value[key] === 'number' && Number.isFinite(value[key]) && value[key] >= 0)
    && (kind === 'global' ? typeof value.updatedAt === 'string' : typeof value.updatedAt === 'number' && Number.isFinite(value.updatedAt));
}
export function readGuestBoard(store: Store): LeaderboardEntry[] {
  const value = read(store, PROFILE_KEYS.board);
  if (value === null) return [];
  if (!Array.isArray(value) || !value.every(item => row(item, 'guest') || row(item, 'user'))) throw new Error('leaderboard_invalid');
  return (value as LeaderboardEntry[]).filter(item => item.kind === 'guest').sort((a, b) => b.gates - a.gates || b.mastered - a.mastered || b.bestStreak - a.bestStreak || b.focus - a.focus || b.updatedAt - a.updatedAt).slice(0, 5);
}
export async function readGlobalBoard(fetcher: typeof fetch = fetch, signal?: AbortSignal): Promise<{ entries: GlobalLeaderboardEntry[]; generatedAt: string }> {
  const response = await fetcher('/api/leaderboard?limit=8', { headers: { accept: 'application/json' }, signal });
  if (!response.ok) throw new Error('leaderboard_unavailable');
  const data: unknown = await response.json();
  if (!object(data) || data.ok !== true || !Array.isArray(data.entries) || !data.entries.every(item => row(item, 'global'))
    || typeof data.generatedAt !== 'string' || !Number.isFinite(Date.parse(data.generatedAt))
    || new Set(data.entries.map(item => item.id)).size !== data.entries.length) throw new Error('leaderboard_invalid');
  return { entries: data.entries.slice(0, 8) as GlobalLeaderboardEntry[], generatedAt: data.generatedAt };
}

export function readAvatarFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { reject(new Error('avatar_invalid')); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('avatar_read_failed'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('avatar_decode_failed'));
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas'); canvas.width = canvas.height = 128;
          const context = canvas.getContext('2d');
          if (!context || !image.width || !image.height) throw new Error('avatar_decode_failed');
          const scale = Math.max(128 / image.width, 128 / image.height), width = image.width * scale, height = image.height * scale;
          context.drawImage(image, (128 - width) / 2, (128 - height) / 2, width, height);
          resolve(validAvatar(canvas.toDataURL('image/webp', 0.82)));
        } catch (error) { reject(error); }
      };
      image.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  });
}
